import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { PostLoginWrapper } from './PostLoginWrapper';
import { Button } from 'primereact/button';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Calendar } from 'primereact/calendar';
import { addLocale } from 'primereact/api';
import { Line } from 'react-chartjs-2';
import { Bar } from 'react-chartjs-2';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { clearAuthSession } from '../authSession';
import et0Img from '../assets/et0.png';
import lluviaImg from '../assets/lluvia.png';
import riegoImg from '../assets/riego.png';
import viñaImg from '../assets/paleta_viña.jpg';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

// Plugin personalizado para dibujar línea de separación antes de "Total"
const separatorLinePlugin = {
    id: 'separatorLine',
    afterDraw: (chart) => {
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        
        // Buscar el índice de "Total" en las etiquetas
        const labels = chart.data.labels || [];
        const totalIndex = labels.findIndex(label => label === 'Total');
        
        if (totalIndex !== -1 && totalIndex > 0) {
            // Obtener la posición X del punto justo antes de "Total"
            const scale = chart.scales.x;
            const xPosition = scale.getPixelForValue(totalIndex - 0.5);
            
            // Dibujar línea vertical
            ctx.save();
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]); // Línea punteada
            ctx.beginPath();
            ctx.moveTo(xPosition, chartArea.top);
            ctx.lineTo(xPosition, chartArea.bottom);
            ctx.stroke();
            ctx.restore();
        }
    }
};

ChartJS.register(separatorLinePlugin);

/**
 * Año de la campaña activa según la fecha de hoy (mismo criterio que el backend).
 * Si hoy >= 1 de junio del año en curso → campaña = ese año; si no → año anterior.
 */
function getActiveCampaignYear() {
    const today = new Date();
    const cutoff = new Date(today.getFullYear(), 5, 1); // 1 de junio (mes 5)
    return today >= cutoff ? today.getFullYear() : today.getFullYear() - 1;
}

/**
 * Límites del calendario de histórico para una campaña concreta (misma ventana que get_campos).
 * Rango seleccionable: 1 jun – 15 sep del año de campaña indicado.
 * Si no se pasa año, usa la campaña activa.
 */
function getActiveCampaignCalendarBounds(campaignYear = null) {
    const year = campaignYear ?? getActiveCampaignYear();
    const minDate = new Date(year, 5, 1);
    const maxDate = new Date(year, 8, 15);
    return { minDate, maxDate, defaultRange: [minDate, maxDate] };
}

export const Field = () => {
    const location = useLocation();
    const { field: fieldFromState } = location.state || {};
    const { id: fieldId } = useParams();
    const navigate = useNavigate();
    const [field, setField] = useState(fieldFromState);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // Campaña seleccionada (año) y campañas disponibles que devuelve get_campos.
    const [selectedCampaignYear, setSelectedCampaignYear] = useState(() => getActiveCampaignYear());
    const [availableCampaignYears, setAvailableCampaignYears] = useState([]);
    // Evita recargar al montar; el efecto de cambio de campaña solo debe actuar al cambiar de año.
    const isFirstCampaignRender = useRef(true);
    // Firma del set de NDVI ya cargado, para no re-pedir imágenes cuando el campo se recarga
    // por motivos ajenos al NDVI (p.ej. añadir un riego recalcula datos_calculados).
    const lastNdviSignatureRef = useRef(null);
    const [isValidatingOwnership, setIsValidatingOwnership] = useState(true);
    const [currentNdviIndex, setCurrentNdviIndex] = useState(0);
    const [selectedImages, setSelectedImages] = useState([]);
    
    // Estados para la funcionalidad de riego
    const [showRiegoModal, setShowRiegoModal] = useState(false);
    const [showHistoricoRiego, setShowHistoricoRiego] = useState(false);
    const [fechaRiego, setFechaRiego] = useState(new Date());
    const [cantidadRiego, setCantidadRiego] = useState(0);
    const [historicoRiego, setHistoricoRiego] = useState([]);
    const [loadingRiego, setLoadingRiego] = useState(false);
    
    // Estados para editar riego
    const [editingRiego, setEditingRiego] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    
    // Estados para confirmación de borrado
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [riegoToDelete, setRiegoToDelete] = useState(null);

    // Estados para la vista de comparativa de imágenes
    const [showComparativa, setShowComparativa] = useState(false);
    const [comparativaFechaInicio, setComparativaFechaInicio] = useState(null);
    const [comparativaFechaFin, setComparativaFechaFin] = useState(null);
    const [comparativaImageUrl, setComparativaImageUrl] = useState(null);
    const [loadingComparativa, setLoadingComparativa] = useState(false);
    const [errorComparativa, setErrorComparativa] = useState('');
    const [comparativaPorcentaje, setComparativaPorcentaje] = useState(null);
    const [comparativaNdviInicio, setComparativaNdviInicio] = useState(null);
    const [comparativaNdviFin, setComparativaNdviFin] = useState(null);
    const [comparativaImgInicio, setComparativaImgInicio] = useState(null);
    const [comparativaImgFin, setComparativaImgFin] = useState(null);

    // Estado para el rango de fechas del calendario (solo dentro de la campaña activa; ver minDate/maxDate en Calendar).
    const [selectedDateRangePrevius, setSelectedDateRangePrevius] = useState(() => {
        return getActiveCampaignCalendarBounds().defaultRange;
    });

    const [selectedDateRange, setSelectedDateRange] = useState(() => {
        return getActiveCampaignCalendarBounds().defaultRange;
    });

    const campaignCalendarBounds = useMemo(() => {
        const { minDate, maxDate } = getActiveCampaignCalendarBounds(selectedCampaignYear);
        return { minDate, maxDate };
    }, [selectedCampaignYear]);

    // get_riegos devuelve TODOS los riegos del campo (de todas las campañas);
    // aquí los acotamos a la ventana de la campaña seleccionada (1-jun → 15-sep).
    const historicoRiegoCampania = useMemo(() => {
        if (!Array.isArray(historicoRiego)) return [];
        const { minDate, maxDate } = campaignCalendarBounds;
        return historicoRiego.filter(riego => {
            const f = new Date(riego.fecha);
            return f >= minDate && f <= maxDate;
        });
    }, [historicoRiego, campaignCalendarBounds]);

    // Ordenamos el array de NDVI de la fecha más reciente a la más antigua y solo nos quedamos con los valores no interpolados.
    const sortedNdviNotInterpolated = useMemo(() => {
        if (field && Array.isArray(field.ndvi)) {
            return [...field.ndvi].filter(ndvi => ndvi.interpolado === 0).sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
        }
        return [];
    }, [field]);

    // Opciones de fechas disponibles para la comparativa — inicio ascendente, fin descendente
    const availableNdviDateOptionsAsc = useMemo(() => {
        return [...sortedNdviNotInterpolated]
            .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
            .map(ndvi => ({
                label: new Date(ndvi.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }),
                value: ndvi.fecha
            }));
    }, [sortedNdviNotInterpolated]);

    const availableNdviDateOptionsDesc = useMemo(() => {
        return [...sortedNdviNotInterpolated]
            .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
            .map(ndvi => ({
                label: new Date(ndvi.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }),
                value: ndvi.fecha
            }));
    }, [sortedNdviNotInterpolated]);

    // Función utilitaria para convertir fecha local a formato YYYY-MM-DD sin problemas de zona horaria
    const getLocalDateString = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Preparación de los datos para el gráfico de NDVI.
    // Se ordenan cronológicamente (del más antiguo al más reciente)
    // Preparación de los datos para el gráfico de NDVI.
    const chartData = useMemo(() => {
        if (!field || !field.ndvi || !Array.isArray(field.ndvi)) return null;
        // Ordenamos cronológicamente (del más antiguo al más reciente)
        const ndviAscendente = [...field.ndvi].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
        const labels = ndviAscendente.map(ndvi => 
            new Date(ndvi.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
        );
        const dataValues = ndviAscendente.map(ndvi => Math.round(ndvi.valor * 1000000) / 1000000);
        
        // Crear arrays de colores basados en el valor interpolado
        const pointBackgroundColors = ndviAscendente.map(ndvi => 
            ndvi.interpolado === 0 ? 'rgb(255, 20, 71)' : 'rgb(124, 166, 255)'
        );
        const pointBorderColors = ndviAscendente.map(ndvi => 
            ndvi.interpolado === 0 ? 'rgb(255, 20, 71)' : 'rgb(124, 166, 255)'
        );
        
        // Crear arrays de tamaños basados en el valor interpolado
        const pointRadiusSizes = ndviAscendente.map(ndvi => 
            ndvi.interpolado === 0 ? 2 : 0
        );
        const pointHoverRadiusSizes = ndviAscendente.map(ndvi => 
            ndvi.interpolado === 0 ? 6 : 4
        );

        // Año a mostrar en el título: el de la campaña seleccionada (no la fecha actual).
        const añoNdvi = selectedCampaignYear;

        return {
            labels,
            datasets: [
                {
                    label: `Índice de vegetación ${añoNdvi}`,
                    data: dataValues,
                    fill: false,
                    backgroundColor: pointBackgroundColors,
                    borderColor: 'rgb(124, 166, 255)',
                    pointBackgroundColor: pointBackgroundColors,
                    pointBorderColor: pointBorderColors,
                    borderWidth: 4, // Línea más gruesa
                    tension: 0,
                    pointRadius: pointRadiusSizes,
                    pointHoverRadius: pointHoverRadiusSizes,
                },
            ],
        };
    }, [field, selectedCampaignYear]);

    const chartOptions = {
        animation: {
            duration: 0 // Desactiva la animación
        },
        stacked: false,
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    boxWidth: 10,
                    boxHeight: 10,
                    padding: 10,
                    color: 'black',
                    pointStyle: 'circle',
                    font: {
                        size: 14
                    }
                },
            },
            title: {
                display: false,
                text: 'Evolución del NDVI',
                font: {
                    size: 20,
                    weight: 'bold'
                },
                color: 'black'
            },
            tooltip: {
                // Fuerza al tooltip a alinearse hacia abajo del punto (lo empuja más arriba).
                yAlign: 'bottom',
                // Aumenta la distancia entre la punta (caret) del tooltip y el punto.
                caretPadding: 15,
                // Posición del tooltip con respecto al punto (puedes probar 'average' o 'nearest')
                position: 'nearest',
                callbacks: {
                    label: function(context) {
                        // 1) Primera línea con el valor de NDVI
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        label += Math.round(context.parsed.y * 1000000) / 1000000;
    
                        // 2) Convertimos esa línea a array (será la primera línea del tooltip)
                        let lines = [label];
    
                        // 3) Obtenemos la recomendación
                        const index = context.dataIndex;
    
                        // 4) Devolvemos el array de líneas
                        return lines;
                    }
                }
            },
            datalabels: {
                display: false
            }
        },
        scales: {
            y: {
                min: 0,
                max: 0.7,
                ticks: {
                    stepSize: 0.1,
                    color: 'black',
                    font: {
                        size: 14
                    }
                },
                title: {
                    display: false,
                    text: 'Índice de vegetación',
                    color: 'black',
                    font: {
                        size: 18
                    }
                }
            },
            x: {
                ticks: {
                    color: 'black',
                    font: {
                        size: 14
                    },
                    maxTicksLimit: 10, // Limitar número de etiquetas mostradas
                    maxRotation: 45, // Rotar etiquetas para mejor legibilidad
                    minRotation: 0
                }
            }
        }
    };
    
    

    // Preparación de datos para la gráfica de datos_clima.
    // Se crean dos barras: una para Evapotranspiración y otra para Precipitación.
    // Además se filtran los datos según el rango de fechas seleccionado.
    const chartDataClima = useMemo(() => {
        if (!field || !field.datos_clima?.historicos_clima || !Array.isArray(field.datos_clima.historicos_clima) || !selectedDateRange || selectedDateRange.length !== 2) return null;
        const climaAscendente = [...field.datos_clima.historicos_clima].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

        let startDate = selectedDateRange[0];
        let endDate = selectedDateRange[1];

        if (selectedDateRange[1] === null){
            startDate = selectedDateRangePrevius[0];
            endDate = selectedDateRangePrevius[1];
        }else{
            setSelectedDateRangePrevius(selectedDateRange);
        }

        const filteredDatosClima = climaAscendente.filter((item) => {
            const fecha = new Date(item.fecha);
            const endDatePlusOne = new Date(endDate);
            endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
            return fecha >= startDate && fecha < endDatePlusOne;
        });

        if (filteredDatosClima.length === 0) return null;

        const labels = filteredDatosClima.map(item => new Date(item.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }));
        const et0Values = filteredDatosClima.map(item => item.et0);
        const precipitacionValues = filteredDatosClima.map(item => item.precipitacion);

        // Procesar riegos para el mismo período
        const riegosValues = labels.map((label, index) => {
            if (index === labels.length - 1) return 0; // No incluir riegos en el total
            const fechaClima = filteredDatosClima[index].fecha;
            const riegoDelDia = historicoRiegoCampania.find(riego => {
                const fechaRiego = new Date(riego.fecha).toISOString().split('T')[0];
                return fechaRiego === fechaClima;
            });
            return riegoDelDia ? riegoDelDia.cantidad : 0;
        });

        const totalEt0 = et0Values.reduce((acc, val) => acc + val, 0);
        const totalPrecipitacion = precipitacionValues.reduce((acc, val) => acc + val, 0);
        const totalRiegos = riegosValues.reduce((acc, val) => acc + val, 0);

        // Guardar los datos diarios originales (sin el total)
        const et0Diarios = [...et0Values];
        const precipitacionDiarios = [...precipitacionValues];
        const riegosDiarios = [...riegosValues];
        const labelsDiarios = [...labels];
        
        // Añadir 'Total' al final de las etiquetas
        labels.push('Total');
        
        // Arrays para totales (solo mostrar el total en la última posición, el resto con 0)
        const totalEt0Array = [...et0Diarios.map(() => 0), totalEt0];
        const totalPrecipitacionArray = [...precipitacionDiarios.map(() => 0), totalPrecipitacion];
        const totalRiegosArray = [...riegosDiarios.map(() => 0), totalRiegos];
        
        // Para los datos diarios, añadir 0 al final (no se mostrará porque el total está en el eje secundario)
        et0Values.push(0);
        precipitacionValues.push(0);
        riegosValues.push(0);

        const datasets = [
            {
                label: 'Evapotranspiración',
                data: et0Values,
                backgroundColor: 'rgb(135, 206, 250)',
                yAxisID: 'y'
            },
            {
                label: 'Precipitación',
                data: precipitacionValues,
                backgroundColor: 'rgb(76, 73, 255)',
                yAxisID: 'y'
            }
        ];

        // Solo añadir dataset de riegos si hay datos
        if (totalRiegos > 0) {
            datasets.push({
                label: 'Riego',
                data: riegosValues,
                backgroundColor: 'rgb(34, 197, 94)',
                yAxisID: 'y'
            });
        }

        // Añadir datasets de totales con eje secundario (solo para el valor del total)
        // Usar los mismos colores que los datos diarios pero con borde para separación visual
        datasets.push({
            label: 'Total ET0',
            data: totalEt0Array,
            backgroundColor: 'rgb(135, 206, 250)',
            borderColor: 'rgb(0, 0, 0)',
            borderWidth: 0,
            yAxisID: 'y1',
            order: 0 // Dibujar primero para que quede atrás
        });
        datasets.push({
            label: 'Total Precipitación',
            data: totalPrecipitacionArray,
            backgroundColor: 'rgb(76, 73, 255)',
            borderColor: 'rgb(0, 0, 0)',
            borderWidth: 0,
            yAxisID: 'y1',
            order: 0
        });
        if (totalRiegos > 0) {
            datasets.push({
                label: 'Total Riego',
                data: totalRiegosArray,
                backgroundColor: 'rgb(34, 197, 94)',
                borderColor: 'rgb(0, 0, 0)',
                borderWidth: 0,
                yAxisID: 'y1',
                order: 0
            });
        }

        return {
            labels,
            datasets
        };
    }, [field, selectedDateRange, historicoRiegoCampania]);

    // Preparación de datos para la gráfica de balance hídrico
    const chartDataBalance = useMemo(() => {
        if (!field || !field.datos_calculados || !Array.isArray(field.datos_calculados) || !selectedDateRange || selectedDateRange.length !== 2) {
            console.log('DEBUG chartDataBalance: Validación inicial falló', {
                tiene_field: !!field,
                tiene_datos_calculados: !!(field && field.datos_calculados),
                es_array: !!(field && field.datos_calculados && Array.isArray(field.datos_calculados)),
                tiene_selectedDateRange: !!selectedDateRange,
                length_selectedDateRange: selectedDateRange?.length
            });
            return null;
        }
        if (!field || !field.datos_clima?.historicos_clima || !Array.isArray(field.datos_clima.historicos_clima) || !selectedDateRange || selectedDateRange.length !== 2) {
            return null;
        }
        const climaAscendente = [...field.datos_clima.historicos_clima].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
        const calculadosAscendente = [...field.datos_calculados].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

        // Aplicar el mismo rango de fechas del calendario que en "Datos históricos"
        let startDate = selectedDateRange[0];
        let endDate = selectedDateRange[1];
        if (selectedDateRange[1] === null) {
            startDate = selectedDateRangePrevius[0];
            endDate = selectedDateRangePrevius[1];
        }

        const inSelectedRange = (fechaRaw) => {
            const fecha = new Date(fechaRaw);
            const endDatePlusOne = new Date(endDate);
            endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
            return fecha >= startDate && fecha < endDatePlusOne;
        };

        const filteredDatosCalculados = calculadosAscendente.filter(item => inSelectedRange(item.fecha));
        const filteredDatosClima = climaAscendente.filter(item => inSelectedRange(item.fecha));

        if (filteredDatosCalculados.length === 0) {
            console.log('DEBUG chartDataBalance: filteredDatosCalculados está vacío', {
                total_calculados: calculadosAscendente.length
            });
            return null;
        }
        if (filteredDatosClima.length === 0) {
            console.log('DEBUG chartDataBalance: filteredDatosClima está vacío', {
                total_clima: climaAscendente.length
            });
            return null;
        }

        const labels = filteredDatosCalculados.map(item =>
            new Date(item.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
        );


        // ETc (Evapotranspiración del cultivo) - en cc, valores positivos hacia arriba
        const etcValues = filteredDatosCalculados.map(item => item.etc * -1); // Convertir de mm a cc

        // Alinear precipitación por fecha para evitar desfases entre arrays de clima y calculados
        const climaByDate = new Map(
            filteredDatosClima.map(item => [getLocalDateString(new Date(item.fecha)), item])
        );
        const precipitacionValues = filteredDatosCalculados.map(item => {
            const climaItem = climaByDate.get(getLocalDateString(new Date(item.fecha)));
            return climaItem?.precipitacion ?? 0;
        });

        // Riego - en cc, valores positivos hacia arriba
        const riegoValues = filteredDatosCalculados.map(item => item.riego);

        // Escorrentía - en cc, valores negativos hacia abajo
        const escorrentiaValues = filteredDatosCalculados.map(item => item.escorrentia); // Simulada como 20% de precipitación

        // Humedad del suelo - línea en bares (eje secundario)
        const humedadValues = filteredDatosCalculados.map(item => item.hsuelo);

        // Potencial hídrico de la hoja - en cc, valores negativos hacia abajo
        const potencialHidricoHojaValues = filteredDatosCalculados.map(item => item.prhhoja);

        return {
            labels,
            datasets: [
                {
                    label: 'ETc',
                    data: etcValues,
                    borderColor: 'rgb(236, 72, 153)', // Rosa/magenta
                    backgroundColor: 'rgba(236, 72, 153, 0.1)',
                    fill: false,
                    borderWidth: 3,
                    pointRadius: 0,
                    pointHoverRadius: 5,
                    yAxisID: 'y',
                    tension: 0.4
                },
                {
                    type: 'bar',
                    label: 'Precipitación', 
                    data: precipitacionValues,
                    backgroundColor: 'rgb(59, 130, 246)', // Azul
                    yAxisID: 'y'
                },
                {
                    type: 'bar',
                    label: 'Riego',
                    data: riegoValues,
                    backgroundColor: 'rgb(34, 197, 94)', // Color verde
                    yAxisID: 'y'
                },
                /*
                {
                    label: 'Escorrentía',
                    data: escorrentiaValues,
                    borderColor: 'rgb(250, 204, 21)', // Amarillo
                    backgroundColor: 'rgba(250, 204, 21, 0.1)',
                    fill: false,
                    borderWidth: 3,
                    pointRadius: 0,
                    pointHoverRadius: 5,
                    yAxisID: 'y',
                    tension: 0.1
                },
                {
                    label: 'Humedad del suelo',
                    data: humedadValues,
                    borderColor: 'rgb(127, 29, 29)', // Marrón oscuro
                    backgroundColor: 'rgba(127, 29, 29, 0.1)',
                    fill: false,
                    borderWidth: 3,
                    pointRadius: 0,
                    pointHoverRadius: 5,
                    yAxisID: 'y1',
                    tension: 0.1
                },
                */
                {
                    label: 'Potencial hídrico de la hoja',
                    data: potencialHidricoHojaValues,
                    borderColor: 'rgb(102, 184, 54)', // Marrón oscuro
                    backgroundColor: 'rgba(127, 29, 29, 0.1)',
                    fill: false,
                    borderWidth: 3,
                    pointRadius: 0,
                    pointHoverRadius: 5,
                    yAxisID: 'y1',
                    tension: 0.4
                }
            ]
        };
    }, [field, selectedDateRange]);

    // Opciones para la gráfica de balance hídrico
    const chartOptionsBalance = {
        animation: {
            duration: 0
        },
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    boxWidth: 10,
                    boxHeight: 10,
                    padding: 15,
                    color: 'black',
                    font: {
                        size: 12
                    }
                }
            },
            title: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.dataset.yAxisID === 'y1') {
                            label += Math.round(context.parsed.y * 100) / 100;
                        } else {
                            label += Math.round(context.parsed.y * 10) / 10; // 1 decimal para cc
                        }
                        return label;
                    }
                }
            },
            datalabels: {
                display: false // Desactivar etiquetas de datos en los puntos
            }
        },
        scales: {
            x: {
                ticks: {
                    color: 'black',
                    font: {
                        size: 12
                    },
                    maxRotation: 45,
                    minRotation: 0
                },
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            },
            y: {
                type: 'linear',
                position: 'left',
                min: 0,
                max: 35,
                ticks: {
                    color: 'black',
                    font: {
                        size: 12
                    },
                    callback: function(value) {
                        return Math.round(value * 10) / 10; // 1 decimal
                    }
                },
                title: {
                    display: true,
                    text: 'ETc y precipitación (mm)',
                    color: 'black',
                    font: {
                        size: 14
                    }
                },
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            },
            y1: {
                type: 'linear',
                position: 'right',
                min: -2,
                max: 0,
                ticks: {
                    color: 'black',
                    font: {
                        size: 12
                    },
                    callback: function(value) {
                        return Math.round(value * 100) / 100; // 2 decimales
                    }
                },
                title: {
                    display: true,
                    text: 'Potencial hídrico de la hoja (MPa)',
                    color: 'black',
                    font: {
                        size: 14
                    }
                },
                grid: {
                    display: false
                }
            }
        }
    };

    // Preparación de datos para la gráfica de balance hídrico
    const chartDataBalanceV2 = useMemo(() => {
        if (!field || !field.datos_calculados || !Array.isArray(field.datos_calculados) || !selectedDateRange || selectedDateRange.length !== 2) {
            console.log('DEBUG chartDataBalanceV2: Validación inicial falló', {
                tiene_field: !!field,
                tiene_datos_calculados: !!(field && field.datos_calculados),
                es_array: !!(field && field.datos_calculados && Array.isArray(field.datos_calculados)),
                tiene_selectedDateRange: !!selectedDateRange,
                length_selectedDateRange: selectedDateRange?.length
            });
            return null;
        }
        if (!field || !field.datos_clima?.historicos_clima || !Array.isArray(field.datos_clima.historicos_clima) || !selectedDateRange || selectedDateRange.length !== 2) {
            return null;
        }
        const climaAscendente = [...field.datos_clima.historicos_clima].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
        const calculadosAscendente = [...field.datos_calculados].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

        // Año de la campaña seleccionada (no la fecha actual), para que la gráfica
        // funcione también al consultar campañas históricas.
        const año_datos = selectedCampaignYear;

        let startDate = new Date(año_datos, 4, 1); // 1 de mayo del año de los datos
        let endDate = new Date(año_datos, 9, 31); // 31 de octubre del año de los datos

        const filteredDatosCalculados = calculadosAscendente.filter((item) => {
            const fecha = new Date(item.fecha);
            const endDatePlusOne = new Date(endDate);
            endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
            return fecha >= startDate && fecha < endDatePlusOne;
        });

        const filteredDatosClima = climaAscendente.filter((item) => {
            const fecha = new Date(item.fecha);
            const endDatePlusOne = new Date(endDate);
            endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
            return fecha >= startDate && fecha < endDatePlusOne;
        });

        if (filteredDatosCalculados.length === 0) {
            console.log('DEBUG chartDataBalanceV2: filteredDatosCalculados está vacío', {
                total_calculados: calculadosAscendente.length,
                startDate,
                endDate,
                año_datos
            });
            return null;
        }
        if (filteredDatosClima.length === 0) {
            console.log('DEBUG chartDataBalanceV2: filteredDatosClima está vacío', {
                total_clima: climaAscendente.length,
                startDate,
                endDate
            });
            return null;
        }
        
        // Validar que datos_calculados tiene elementos y que el primer elemento tiene las propiedades necesarias
        if (!field.datos_calculados || field.datos_calculados.length === 0) {
            console.log('DEBUG chartDataBalanceV2: field.datos_calculados está vacío');
            return null;
        }
        if (!field.datos_calculados[0] || field.datos_calculados[0].prhsuelo_ccampo === undefined || field.datos_calculados[0].prhsuelo_marchitez === undefined) {
            console.log('DEBUG chartDataBalanceV2: field.datos_calculados[0] no tiene las propiedades necesarias', field.datos_calculados[0]);
            return null;
        }

        const labels = filteredDatosCalculados.map(item => {
            const fecha = new Date(item.fecha);
            return fecha.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
        });


        // ETc (Evapotranspiración del cultivo) - en cc, valores positivos hacia arriba
        const etcValues = filteredDatosCalculados.map(item => item.etc * -1); // Convertir de mm a cc

        // Alinear precipitación por fecha para evitar desfases entre arrays de clima y calculados
        const climaByDate = new Map(
            filteredDatosClima.map(item => [getLocalDateString(new Date(item.fecha)), item])
        );
        const precipitacionValues = filteredDatosCalculados.map(item => {
            const climaItem = climaByDate.get(getLocalDateString(new Date(item.fecha)));
            return climaItem?.precipitacion ?? 0;
        });

        // Riego - en cc, valores positivos hacia arriba
        const riegoValues = filteredDatosCalculados.map(item => item.riego);

        // Escorrentía - en cc, valores negativos hacia abajo
        const escorrentiaValues = filteredDatosCalculados.map(item => item.escorrentia); // Simulada como 20% de precipitación

        // Humedad del suelo - línea en bares (eje secundario)
        const humedadValues = filteredDatosCalculados.map(item => item.hsuelo);

        // Potencial hídrico de la hoja - en cc, valores negativos hacia abajo
        const potencialHidricoHojaValues = filteredDatosCalculados.map(item => item.prhhoja);

        // Potencial hídrico del campo - en cc, valores negativos hacia abajo
        const potencialHidricoCampoValues = filteredDatosCalculados.map(item => item.prhsuelo);

        // Valores de referencia constantes para las líneas de capacidad de campo y marchitez
        const prhsuelo_ccampo = field.datos_calculados[0].prhsuelo_ccampo;
        const prhsuelo_marchitez = field.datos_calculados[0].prhsuelo_marchitez;
        const potencial_hidrico_inicio_estres = field.potencial_hidrico_inicio_estres;
        const potencial_hidrico_maximo_estres = field.potencial_hidrico_maximo_estres;
        
        // Crear arrays con valores constantes para las líneas de referencia
        const prhsueloCcampoValues = labels.map(() => prhsuelo_ccampo);
        const prhsueloMarchitezValues = labels.map(() => prhsuelo_marchitez);
        const potencialHidricoInicioEstresValues = labels.map(() => potencial_hidrico_inicio_estres);
        const potencialHidricoMaximoEstresValues = labels.map(() => potencial_hidrico_maximo_estres);

        return {
            labels,
            datasets: [
                {
                    label: 'ETc',
                    data: etcValues,
                    borderColor: 'rgb(236, 72, 153)', // Rosa/magenta
                    backgroundColor: 'rgba(236, 72, 153, 0.1)',
                    fill: false,
                    borderWidth: 3,
                    pointRadius: 0,
                    pointHoverRadius: 5,
                    yAxisID: 'y',
                    tension: 0.4
                },
                {
                    type: 'bar',
                    label: 'Precipitación', 
                    data: precipitacionValues,
                    backgroundColor: 'rgb(59, 130, 246)', // Azul
                    yAxisID: 'y'
                },
                {
                    type: 'bar',
                    label: 'Riego',
                    data: riegoValues,
                    backgroundColor: 'rgb(34, 197, 94)', // Color verde
                    yAxisID: 'y'
                },
                /*
                {
                    label: 'Escorrentía',
                    data: escorrentiaValues,
                    borderColor: 'rgb(250, 204, 21)', // Amarillo
                    backgroundColor: 'rgba(250, 204, 21, 0.1)',
                    fill: false,
                    borderWidth: 3,
                    pointRadius: 0,
                    pointHoverRadius: 5,
                    yAxisID: 'y',
                    tension: 0.1
                },
                {
                    label: 'Humedad del suelo',
                    data: humedadValues,
                    borderColor: 'rgb(127, 29, 29)', // Marrón oscuro
                    backgroundColor: 'rgba(127, 29, 29, 0.1)',
                    fill: false,
                    borderWidth: 3,
                    pointRadius: 0,
                    pointHoverRadius: 5,
                    yAxisID: 'y1',
                    tension: 0.1
                },
                {
                    label: 'Potencial hídrico de la hoja',
                    data: potencialHidricoHojaValues,
                    borderColor: 'rgb(102, 184, 54)', // Marrón oscuro
                    backgroundColor: 'rgba(127, 29, 29, 0.1)',
                    fill: false,
                    borderWidth: 3,
                    pointRadius: 0,
                    pointHoverRadius: 5,
                    yAxisID: 'y1',
                    tension: 0.4
                },
                */
                {
                    label: 'Potencial hídrico del campo',
                    data: potencialHidricoCampoValues,
                    borderColor: 'rgb(102, 184, 54)', // Verde
                    backgroundColor: 'rgba(127, 29, 29, 0.1)',
                    fill: false,
                    borderWidth: 3,
                    pointRadius: 0,
                    pointHoverRadius: 5,
                    yAxisID: 'y1',
                    tension: 0.4
                },
                {
                    label: 'prhsuelo_ccampo',
                    data: prhsueloCcampoValues,
                    borderColor: 'rgb(0, 123, 255)', // Azul
                    backgroundColor: 'rgba(0, 123, 255, 0.1)',
                    fill: false,
                    borderWidth: 2,
                    borderDash: [5, 5], // Línea punteada
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    yAxisID: 'y1',
                    tension: 0
                },
                {
                    label: 'prhsuelo_marchitez',
                    data: prhsueloMarchitezValues,
                    borderColor: 'rgb(220, 53, 69)', // Rojo
                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                    fill: false,
                    borderWidth: 2,
                    borderDash: [5, 5], // Línea punteada
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    yAxisID: 'y1',
                    tension: 0
                },
                {
                    label: 'potencial_hidrico_inicio_estres',
                    data: potencialHidricoInicioEstresValues,
                    borderColor: 'rgb(220, 53, 69)', // Rojo
                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                    fill: false,
                    borderWidth: 2,
                    borderDash: [5, 5], // Línea punteada
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    yAxisID: 'y1',
                    tension: 0
                },
                {
                    label: 'potencial_hidrico_maximo_estres',
                    data: potencialHidricoMaximoEstresValues,
                    borderColor: 'rgb(220, 53, 69)', // Rojo
                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                    fill: false,
                    borderWidth: 2,
                    borderDash: [5, 5], // Línea punteada
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    yAxisID: 'y1',
                    tension: 0
                }
            ]
        };
    }, [field, selectedDateRange, selectedCampaignYear]);

    // Opciones para la gráfica de balance hídrico
    const chartOptionsBalanceV2 = {
        animation: {
            duration: 0
        },
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    boxWidth: 10,
                    boxHeight: 10,
                    padding: 15,
                    color: 'black',
                    font: {
                        size: 12
                    }
                }
            },
            title: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.dataset.yAxisID === 'y1') {
                            label += Math.round(context.parsed.y * 100) / 100;
                        } else {
                            label += Math.round(context.parsed.y * 10) / 10; // 1 decimal para cc
                        }
                        return label;
                    }
                }
            },
            datalabels: {
                display: false // Desactivar etiquetas de datos en los puntos
            }
        },
        scales: {
            x: {
                ticks: {
                    color: 'black',
                    font: {
                        size: 12
                    },
                    maxRotation: 45,
                    minRotation: 0
                },
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            },
            y: {
                type: 'linear',
                position: 'left',
                min: 0,
                max: 35,
                ticks: {
                    color: 'black',
                    font: {
                        size: 12
                    },
                    callback: function(value) {
                        return Math.round(value * 10) / 10; // 1 decimal
                    }
                },
                title: {
                    display: true,
                    text: 'ETc y precipitación (mm)',
                    color: 'black',
                    font: {
                        size: 14
                    }
                },
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            },
            y1: {
                type: 'linear',
                position: 'right',
                min: 0,
                max: (field?.datos_calculados?.[0]?.prhsuelo_marchitez ?? 5) + 1,
                ticks: {
                    color: 'black',
                    font: {
                        size: 12
                    },
                    callback: function(value) {
                        return Math.round(value * 100) / 100; // 2 decimales
                    }
                },
                title: {
                    display: true,
                    text: 'Potencial hídrico de la hoja (bares)',
                    color: 'black',
                    font: {
                        size: 14
                    }
                },
                grid: {
                    display: false
                }
            }
        }
    };

    // Opciones para la gráfica de datos_clima con dos ejes Y.
    const chartOptionsClima = {
        animation: {
            duration: 0
        },
        stacked: false,
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        plugins: {
            legend: {
                position: 'top',
                onClick: function(e, legendItem, legend) {
                    const index = legendItem.datasetIndex;
                    const chart = legend.chart;
                    const meta = chart.getDatasetMeta(index);
                    
                    // Obtener el label del dataset clicado
                    const clickedLabel = chart.data.datasets[index].label;
                    
                    // Determinar el nuevo estado (ocultar si está visible, mostrar si está oculto)
                    const willBeHidden = meta.hidden === null || meta.hidden === false;
                    
                    // Mapeo de labels diarios a labels de total
                    const labelMap = {
                        'Evapotranspiración': 'Total ET0',
                        'Precipitación': 'Total Precipitación',
                        'Riego': 'Total Riego'
                    };
                    
                    const totalLabel = labelMap[clickedLabel];
                    
                    // Si existe un total correspondiente, cambiar su visibilidad también
                    if (totalLabel) {
                        const totalIndex = chart.data.datasets.findIndex(ds => ds.label === totalLabel);
                        if (totalIndex !== -1) {
                            const totalMeta = chart.getDatasetMeta(totalIndex);
                            // Ocultar/mostrar el total junto con el diario
                            totalMeta.hidden = willBeHidden;
                        }
                    }
                    
                    // Comportamiento por defecto: ocultar/mostrar el dataset clicado
                    meta.hidden = willBeHidden;
                    chart.update();
                },
                labels: {
                    usePointStyle: true,
                    boxWidth: 10,
                    boxHeight: 10,
                    padding: 10,
                    color: 'black',
                    pointStyle: 'circle',
                    font: {
                        size: 14
                    },
                    filter: function(legendItem, chartData) {
                        // Ocultar los datasets que tienen "Total" en su label
                        return !legendItem.text.includes('Total');
                    }
                }
            },
            title: {
                display: false,
                text: 'Datos de Clima',
                font: {
                    size: 16,
                    weight: 'bold'
                },
                color: 'black'
            },
            datalabels: {
                display: false
            }
        },
        scales: {
            y: {
                type: 'linear',
                position: 'left',
                ticks: {
                    color: 'black',
                    font: {
                        size: 14
                    }
                },
                title: {
                    display: true,
                    text: 'mm (diario)',
                    color: 'black',
                    font: {
                        size: 14
                    }
                },
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            },
            y1: {
                type: 'linear',
                position: 'right',
                ticks: {
                    color: 'black',
                    font: {
                        size: 14
                    }
                },
                title: {
                    display: true,
                    text: 'mm (total acumulado)',
                    color: 'black',
                    font: {
                        size: 14
                    }
                },
                grid: {
                    display: false // No mostrar grid para el eje secundario
                }
            },
            x: {
                ticks: {
                    color: 'black',
                    font: {
                        size: 14
                    }
                },
                grid: {
                    display: false
                }
            }
        }
    };

    // Preparación de datos para la tabla de NDVI (orden ascendente por fecha)
    const ndviAscendenteTable = useMemo(() => {
        if (!field || !field.ndvi || !Array.isArray(field.ndvi)) return [];
        return [...field.ndvi].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
    }, [field]);

    // Preparación de datos para la tabla de datos de clima (orden ascendente por fecha)
    const datosClimaAscendenteTable = useMemo(() => {
        if (!field || !field.datos_clima?.predicciones || !Array.isArray(field.datos_clima.predicciones)) return [];
        return [...field.datos_clima.predicciones]
            .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
            .slice(2);
    }, [field]);

    // Añadir columna de déficit acumulado de riego (ETc - precipitación acumulado)
    // Si el balance diario (ETc - precipitación) es negativo, se acumula como excedente
    // y no se recomienda riego hasta que el acumulado vuelva a ser positivo.
    const datosClimaConDeficit = useMemo(() => {
        let acumulado = 0;
        return datosClimaAscendenteTable.map((item) => {
            const et0 = item.et0 ?? 0; // en predicciones ya viene como ETc (et0*Kc)
            const precip = item.precipitacion ?? 0;
            const daily = et0 - precip;
            // acumulamos el balance, permitiendo valores negativos para "reservas" de agua
            if (acumulado > 0) {acumulado = 0;}
            acumulado += daily;
            const deficitAcumulado = Math.max(acumulado, 0); // para mostrar nunca negativo
            return {
                ...item,
                deficitAcumulado,
            };
        });
    }, [datosClimaAscendenteTable]);

    // Cálculo total para el riego: suma de todas las ETc menos la suma de la precipitación.
    // Si el resultado es negativo, se mostrará 0.
    const totalRiego = useMemo(() => {
        if (!datosClimaAscendenteTable.length) return 0;
        const totalEt0 = datosClimaAscendenteTable.reduce((acc, item) => acc + item.et0, 0);
        const totalPrecipitacion = datosClimaAscendenteTable.reduce((acc, item) => acc + item.precipitacion, 0);
        const diff = totalEt0 - totalPrecipitacion;
        return diff < 0 ? 0 : Math.round(diff * 100) / 100;
    }, [datosClimaAscendenteTable]);

    const totalEt0 = useMemo(() => {
        if (!datosClimaAscendenteTable.length) return 0;
        const totalEt0 = datosClimaAscendenteTable.reduce((acc, item) => acc + item.et0, 0);
        return Math.round(totalEt0 * 0.5 * 10) / 10;
    }, [datosClimaAscendenteTable]);

    // Plantillas para formatear las columnas de la tabla de NDVI
    const fechaBodyTemplate = (rowData) => {
        return new Date(rowData.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const valorBodyTemplate = (rowData) => {
        return Math.round(rowData.valor * 1000000) / 1000000;
    };

    // Plantillas para formatear las columnas de la tabla de datos de clima
    const fechaClimaBodyTemplate = (rowData) => {
        return new Date(rowData.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const et0BodyTemplate = (rowData) => {
        return Math.round((rowData.et0 ?? 0) * 100) / 100;
    };

    const precipitacionBodyTemplate = (rowData) => {
        return Math.round((rowData.precipitacion ?? 0) * 100) / 100;
    };

    // Déficit acumulado de riego (ETc - precipitación acumulado)
    const et0LessPrecipitacionBodyTemplate = (rowData) => {
        const value = rowData.deficitAcumulado ?? 0;
        return Math.round(value * 100) / 100;
    };

    // Plantillas para formatear las columnas del histórico de riego
    const fechaRiegoBodyTemplate = (rowData) => {
        return new Date(rowData.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const cantidadRiegoBodyTemplate = (rowData) => {
        return `${rowData.cantidad} mm`;
    };

    // Plantilla para botones de acción en histórico de riego
    const accionesRiegoBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2 justify-content-center">
                <Button 
                    icon="pi pi-pencil" 
                    className="p-button-rounded p-button-text p-button-warning"
                    tooltip="Editar"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => handleEditRiego(rowData)}
                    disabled={loadingRiego}
                />
                <Button 
                    icon="pi pi-trash" 
                    className="p-button-rounded p-button-text p-button-danger"
                    tooltip="Borrar"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => handleShowDeleteConfirm(rowData)}
                    disabled={loadingRiego}
                />
            </div>
        );
    };

    const refreshAccessToken = async () => {
        try {
            const refreshToken = localStorage.getItem('refreshToken');
            const response = await fetch('/vitisad/refresh_token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ refreshToken })
            });
            
            if (response.ok) {
                const data = await response.json();
                return data.accessToken;
            } else {
                return null;
            }
        } catch (error) {
            console.error("Error al renovar el token de acceso:", error);
            return null;
        }
    };

    const handleLogout = () => {
        clearAuthSession();
        navigate('/login');
    };

    // Función para recargar los datos del campo desde el servidor
    // Sincroniza el selector de campañas con lo que devuelve get_campos.
    const applyCampaignMeta = (data) => {
        if (Array.isArray(data?.available_campaign_years)) {
            setAvailableCampaignYears(data.available_campaign_years);
        }
        // El backend confirma la campaña realmente servida (puede caer a la actual
        // si el año pedido no existe). Solo actualizamos si difiere para evitar bucles.
        if (typeof data?.campaign_year === 'number') {
            setSelectedCampaignYear(prev => (prev === data.campaign_year ? prev : data.campaign_year));
        }
    };

    const reloadFieldData = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
                handleLogout();
                return false;
            }

            const response = await fetch(`/vitisad/get_campos?campaign_year=${selectedCampaignYear}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                applyCampaignMeta(data);
                if (data.data && Array.isArray(data.data)) {
                    // Buscar el campo específico en la lista de campos del usuario
                    const targetField = data.data.find(f => f.id.toString() === fieldId);

                    if (targetField) {
                        setField(targetField);
                        // Recargar también el histórico de riegos usando el fieldId
                        await loadHistoricoRiego(true, fieldId);
                        return true;
                    } else {
                        setError('No se encontró el campo');
                        return false;
                    }
                } else {
                    setError('Error al obtener los campos del usuario');
                    return false;
                }
            } else if (response.status === 401) {
                const newAccessToken = await refreshAccessToken();
                if (newAccessToken) {
                    localStorage.setItem('accessToken', newAccessToken);
                    return await reloadFieldData();
                } else {
                    handleLogout();
                    return false;
                }
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Error al recargar los datos del campo');
                return false;
            }
        } catch (error) {
            console.error('Error recargando datos del campo:', error);
            setError('Error de conexión al recargar los datos');
            return false;
        }
    };


    // Función para validar la propiedad del campo
    const validateFieldOwnership = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
                handleLogout();
                return false;
            }

            // Si tenemos el campo desde el state y el ID coincide, validamos la propiedad
            if (field && field.id && field.id.toString() === fieldId) {
                setIsValidatingOwnership(false);
                // El campo del state es la campaña actual; recargamos en segundo plano
                // para obtener datos frescos y las campañas disponibles (selector).
                reloadFieldData();
                return true;
            }

            // Si no tenemos el campo, obtenemos todos los campos del usuario para validar la propiedad
            const response = await fetch(`/vitisad/get_campos?campaign_year=${selectedCampaignYear}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                applyCampaignMeta(data);
                if (data.data && Array.isArray(data.data)) {
                    // Buscar el campo específico en la lista de campos del usuario
                    const targetField = data.data.find(f => f.id.toString() === fieldId);
                    
                    if (targetField) {
                        setField(targetField);
                        setIsValidatingOwnership(false);
                        return true;
                    } else {
                        setError('No tienes permiso para acceder a este campo o el campo no existe');
                        setIsValidatingOwnership(false);
                        return false;
                    }
                } else {
                    setError('Error al obtener los campos del usuario');
                    setIsValidatingOwnership(false);
                    return false;
                }
            } else if (response.status === 401) {
                const newAccessToken = await refreshAccessToken();
                if (newAccessToken) {
                    localStorage.setItem('accessToken', newAccessToken);
                    return await validateFieldOwnership();
                } else {
                    handleLogout();
                    return false;
                }
            } else if (response.status === 403) {
                setError('No tienes permiso para acceder a este campo');
                setIsValidatingOwnership(false);
                return false;
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Error al validar el acceso al campo');
                setIsValidatingOwnership(false);
                return false;
            }
        } catch (error) {
            console.error('Error validating field ownership:', error);
            setError('Error de conexión al validar el acceso');
            setIsValidatingOwnership(false);
            return false;
        }
    };

    // Función para añadir riego
    const handleAddRiego = async () => {
        if (!cantidadRiego || cantidadRiego <= 0) {
            setError('La cantidad de riego debe ser mayor a 0');
            return;
        }

        setLoadingRiego(true);
        
        const riegoData = {
            campo_id: field.id,
            fecha: getLocalDateString(fechaRiego), // Usar fecha local
            cantidad: cantidadRiego
        };
        
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
                handleLogout();
                return;
            }

            const response = await fetch('/vitisad/post_riego', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify(riegoData)
            });

            if (response.ok) {
                const data = await response.json();
                handleCloseRiegoModal();
                setError(''); // Limpiar errores
                // Asegurar que el scroll esté restaurado
                document.body.style.overflow = 'auto';
                // Recargar los datos del campo
                await reloadFieldData();
            } else if (response.status === 401) {
                const newAccessToken = await refreshAccessToken();
                if (newAccessToken) {
                    localStorage.setItem('accessToken', newAccessToken);
                    // Reintentar la petición
                    handleAddRiego();
                } else {
                    handleLogout();
                }
            } else {
                const errorData = await response.json();
                console.log('Error al añadir riego:', errorData);
                setError(errorData.error || 'Error al añadir el riego');
            }
        } catch (error) {
            console.error('Error en handleAddRiego:', error);
            setError('Error de conexión: ' + error.message);
        } finally {
            setLoadingRiego(false);
        }
    };

    // Función para cargar el histórico de riegos
    const loadHistoricoRiego = async (keepLoading = false, campoIdOverride = null) => {
        if (!keepLoading) {
            setLoadingRiego(true);
        }
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
                handleLogout();
                return;
            }

            const campoIdToUse = campoIdOverride || (field && field.id) || fieldId;
            if (!campoIdToUse) {
                return;
            }

            const response = await fetch(`/vitisad/get_riegos?campo_id=${campoIdToUse}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                
                // Intentar diferentes estructuras de respuesta
                let riegosData = [];
                if (data.riegos) {
                    riegosData = data.riegos;
                } else if (Array.isArray(data)) {
                    riegosData = data;
                } else if (data.data) {
                    riegosData = data.data;
                }
                
                setHistoricoRiego(riegosData);
                setError(''); // Limpiar errores
            } else if (response.status === 401) {
                const newAccessToken = await refreshAccessToken();
                if (newAccessToken) {
                    localStorage.setItem('accessToken', newAccessToken);
                    // Reintentar la petición
                    await loadHistoricoRiego(keepLoading);
                } else {
                    handleLogout();
                }
            } else {
                const errorData = await response.json();
                console.log('Error del servidor:', errorData);
                setError(errorData.error || 'Error al cargar el histórico de riegos');
            }
        } catch (error) {
            console.error('Error en loadHistoricoRiego:', error);
            setError('Error de conexión: ' + error.message);
        } finally {
            if (!keepLoading) {
                setLoadingRiego(false);
            }
        }
    };

    // Función para mostrar el histórico
    const handleShowHistorico = () => {
        setShowHistoricoRiego(true);
        loadHistoricoRiego();
        // Desactivar scroll del body
        document.body.style.overflow = 'hidden';
    };

    // Función para abrir modal de edición
    const handleEditRiego = (riego) => {
        setEditingRiego(riego);
        setIsEditMode(true);
        setFechaRiego(new Date(riego.fecha));
        setCantidadRiego(riego.cantidad);
        setShowRiegoModal(true);
    };

    // Función para actualizar riego
    const handleUpdateRiego = async () => {
        if (!cantidadRiego || cantidadRiego <= 0) {
            setError('La cantidad de riego debe ser mayor a 0');
            return;
        }

        setLoadingRiego(true);
        
        // Cerrar modal inmediatamente
        handleCloseRiegoModal();
        
        const riegoData = {
            campo_id: field.id,
            fecha: getLocalDateString(fechaRiego), // Usar fecha local
            cantidad: cantidadRiego
        };
        
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
                setLoadingRiego(false);
                handleLogout();
                return;
            }

            const response = await fetch(`/vitisad/put_riego/${editingRiego.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify(riegoData)
            });

            if (response.ok) {
                const data = await response.json();
                
                setError('');
                // Recargar los datos del campo
                await reloadFieldData();
            } else if (response.status === 401) {
                const newAccessToken = await refreshAccessToken();
                if (newAccessToken) {
                    localStorage.setItem('accessToken', newAccessToken);
                    // Reintentar la petición (no cambiar loadingRiego aquí)
                    await handleUpdateRiego();
                    return; // Salir para evitar setLoadingRiego(false) duplicado
                } else {
                    setLoadingRiego(false);
                    handleLogout();
                    return;
                }
            } else {
                const errorData = await response.json();
                console.log('Error al actualizar riego:', errorData);
                setError(errorData.error || 'Error al actualizar el riego');
            }
        } catch (error) {
            console.error('Error en handleUpdateRiego:', error);
            setError('Error de conexión: ' + error.message);
        } finally {
            setLoadingRiego(false);
        }
    };

    // Función para mostrar confirmación de borrado
    const handleShowDeleteConfirm = (riego) => {
        setRiegoToDelete(riego);
        setShowDeleteConfirm(true);
        // Desactivar scroll del body
        document.body.style.overflow = 'hidden';
    };

    // Función para confirmar el borrado
    const handleConfirmDelete = async () => {
        if (!riegoToDelete) return;

        setLoadingRiego(true);
        setShowDeleteConfirm(false);
        
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
                handleLogout();
                return;
            }

            const response = await fetch(`/vitisad/delete_riego/${riegoToDelete.id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setShowDeleteConfirm(false);
                setRiegoToDelete(null);
                document.body.style.overflow = 'auto';
                setError('');
                // Recargar los datos del campo
                await reloadFieldData();
            } else if (response.status === 401) {
                const newAccessToken = await refreshAccessToken();
                if (newAccessToken) {
                    localStorage.setItem('accessToken', newAccessToken);
                    handleConfirmDelete(); // Reintentar
                } else {
                    handleLogout();
                }
            } else {
                const errorData = await response.json();
                console.log('Error al borrar riego:', errorData);
                setError(errorData.error || 'Error al borrar el riego');
            }
        } catch (error) {
            console.error('Error en handleConfirmDelete:', error);
            setError('Error de conexión: ' + error.message);
        } finally {
            setLoadingRiego(false);
            setRiegoToDelete(null);
            // Reactivar scroll del body
            document.body.style.overflow = 'auto';
        }
    };

    // Función para cancelar el borrado
    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
        setRiegoToDelete(null);
        // Reactivar scroll del body
        document.body.style.overflow = 'auto';
    };

    // Función para cerrar modal y limpiar estado
    const handleCloseRiegoModal = () => {
        setShowRiegoModal(false);
        // Reactivar scroll del body inmediatamente
        document.body.style.overflow = 'auto';
        setTimeout(() => {
            setIsEditMode(false);
            setEditingRiego(null);
            setCantidadRiego(0);
            setFechaRiego(new Date());
            setError('');
        }, 500);
    };

    // Función para cargar imágenes:
    // - Al inicio se carga solo 1 imagen.
    // - Al pulsar el botón, se cargan de 3 en 3.
    const loadNextImages = async () => {
        if (!field) {
            setError('No se encontró información del campo.');
            setIsLoading(false);
            return;
        }
        
        const numImagesToLoad = currentNdviIndex === 0 ? 1 : 3;
        
        if (currentNdviIndex >= sortedNdviNotInterpolated.length) return;

        setIsLoading(true);

        const nextNdvis = sortedNdviNotInterpolated.slice(currentNdviIndex, currentNdviIndex + numImagesToLoad);
        const nuevasImagenes = [];

        for (const ndvi of nextNdvis) {
            if (selectedImages.some(img => img.fecha === ndvi.fecha)) {
                continue;
            }
            try {
                const accessToken = localStorage.getItem('accessToken');
                if (!accessToken) {
                    handleLogout();
                    return;
                }

                const response = await fetch(
                    `/vitisad/get_imagen?campo_id=${field.id}&fecha=${encodeURIComponent(ndvi.fecha)}&aux=${encodeURIComponent(new Date().toISOString())}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${accessToken}`
                        }
                    }
                );

                const contentType = response.headers.get('Content-Type');
                if (contentType && contentType.includes('image')) {
                    const blob = await response.blob();
                    const imageURL = URL.createObjectURL(blob);
                    nuevasImagenes.push({ fecha: ndvi.fecha, url: imageURL });
                } else {
                    const data = await response.json();
                    switch (response.status) {
                        case 200:
                            nuevasImagenes.push({ fecha: ndvi.fecha, url: data });
                            break;
                        case 401:
                            const newAccessToken = await refreshAccessToken();
                            if (newAccessToken) {
                                localStorage.setItem('accessToken', newAccessToken);
                                const retryResponse = await fetch(
                                    `/vitisad/get_imagen?campo_id=${field.id}&fecha=${encodeURIComponent(ndvi.fecha)}`,
                                    {
                                        method: 'GET',
                                        headers: {
                                            'Content-Type': 'application/json',
                                            'Authorization': `Bearer ${newAccessToken}`
                                        }
                                    }
                                );
                                const retryContentType = retryResponse.headers.get('Content-Type');
                                if (retryContentType && retryContentType.includes('image')) {
                                    const blobRetry = await retryResponse.blob();
                                    const imageURLRetry = URL.createObjectURL(blobRetry);
                                    nuevasImagenes.push({ fecha: ndvi.fecha, url: imageURLRetry });
                                } else {
                                    setError('Error al cargar la imagen después de renovar el token.');
                                }
                            } else {
                                handleLogout();
                                return;
                            }
                            break;
                        case 403:
                            handleLogout();
                            return;
                        default:
                            setError(data.error || 'Error desconocido');
                    }
                }
            } catch (error) {
                setError('Error de conexión: ' + error.message);
            }
        }

        setSelectedImages(prev => {
            // Evitar duplicados: solo agregar imágenes que no estén ya en el array
            const fechasExistentes = new Set(prev.map(img => img.fecha));
            const imagenesSinDuplicados = nuevasImagenes.filter(img => !fechasExistentes.has(img.fecha));
            return [...prev, ...imagenesSinDuplicados];
        });
        setCurrentNdviIndex(prev => prev + nextNdvis.length);
        setIsLoading(false);
    };

    // Validar propiedad del campo al cargar el componente
    useEffect(() => {
        const initializeField = async () => {
            if (!fieldId) {
                setError('ID de campo no válido');
                setIsValidatingOwnership(false);
                setIsLoading(false);
                return;
            }

            const isValid = await validateFieldOwnership();
            if (!isValid) {
                setIsLoading(false);
            }
        };

        initializeField();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldId, navigate]);

    // Se cargan inicialmente las imágenes (en este caso 1 imagen)
    useEffect(() => {
        if (isValidatingOwnership || !field) return;

        const signature = sortedNdviNotInterpolated.map(n => n.fecha).join(',');
        // Si el NDVI no cambió (p.ej. recarga tras añadir un riego), no volver a pedir imágenes.
        if (signature === lastNdviSignatureRef.current) {
            setIsLoading(false);
            return;
        }
        lastNdviSignatureRef.current = signature;

        if (sortedNdviNotInterpolated.length > 0) {
            loadNextImages();
        } else {
            setIsLoading(false);
            // Si el campo no tiene datos aún (recién creado), no mostrar error
            if (field.ndvi && field.ndvi.length > 0) {
                setError('No se encontró información de NDVI.');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field, sortedNdviNotInterpolated, isValidatingOwnership]);

    // Efecto de limpieza para restaurar el scroll al desmontar el componente
    useEffect(() => {
        return () => {
            // Restaurar scroll del body al desmontar el componente
            document.body.style.overflow = 'auto';
        };
    }, []);

    // Cargar riegos al montar el componente para mostrarlos en la gráfica de clima
    useEffect(() => {
        if (!isValidatingOwnership && field && field.id) {
            loadHistoricoRiego(true); // Cargar en segundo plano sin mostrar loading
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field, isValidatingOwnership]);

    // Al cambiar de campaña: recargar los datos de ese año y resetear vista (fechas e imágenes).
    useEffect(() => {
        if (isFirstCampaignRender.current) {
            isFirstCampaignRender.current = false;
            return;
        }
        const { defaultRange } = getActiveCampaignCalendarBounds(selectedCampaignYear);
        setSelectedDateRange(defaultRange);
        setSelectedDateRangePrevius(defaultRange);
        setSelectedImages([]);
        setCurrentNdviIndex(0);
        setIsLoading(true);
        reloadFieldData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCampaignYear]);

    const handleNextNdvi = () => {
        if (currentNdviIndex < sortedNdviNotInterpolated.length) {
            loadNextImages();
        }
    };

    const handleCalcularComparativa = async () => {
        if (!comparativaFechaInicio || !comparativaFechaFin) return;
        if (new Date(comparativaFechaInicio) >= new Date(comparativaFechaFin)) {
            setErrorComparativa('La fecha de inicio debe ser anterior a la fecha de fin.');
            return;
        }
        setLoadingComparativa(true);
        setErrorComparativa('');
        setComparativaImageUrl(null);
        setComparativaPorcentaje(null);
        setComparativaNdviInicio(null);
        setComparativaNdviFin(null);
        setComparativaImgInicio(null);
        setComparativaImgFin(null);
        try {
            let accessToken = localStorage.getItem('accessToken');
            if (!accessToken) { handleLogout(); return; }

            // Helper: fetch con refresco automático de token en 401
            const fetchConToken = async (url) => {
                let r = await fetch(url, {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                });
                if (r.status === 401) {
                    const nuevoToken = await refreshAccessToken();
                    if (!nuevoToken) { handleLogout(); return null; }
                    localStorage.setItem('accessToken', nuevoToken);
                    accessToken = nuevoToken;
                    r = await fetch(url, {
                        method: 'GET',
                        headers: { 'Authorization': `Bearer ${accessToken}` }
                    });
                }
                if (r.status === 403) { handleLogout(); return null; }
                return r;
            };

            // Helper: obtener imagen individual como object URL
            const fetchImagen = async (fecha) => {
                const r = await fetchConToken(
                    `/vitisad/get_imagen?campo_id=${field.id}&fecha=${encodeURIComponent(fecha)}&aux=${encodeURIComponent(new Date().toISOString())}`
                );
                if (!r) return null;
                const ct = r.headers.get('Content-Type');
                if (ct && ct.includes('image')) {
                    return URL.createObjectURL(await r.blob());
                }
                return null;
            };

            // Las tres llamadas en paralelo
            const [responseDiff, urlImgInicio, urlImgFin] = await Promise.all([
                fetchConToken(`/vitisad/get_comparativa?campo_id=${field.id}&fecha_inicio=${encodeURIComponent(comparativaFechaInicio)}&fecha_fin=${encodeURIComponent(comparativaFechaFin)}`),
                fetchImagen(comparativaFechaInicio),
                fetchImagen(comparativaFechaFin)
            ]);

            if (!responseDiff) return;
            if (!responseDiff.ok) {
                const data = await responseDiff.json();
                setErrorComparativa(data.error || 'Error al generar la comparativa');
                return;
            }

            const porcentaje = responseDiff.headers.get('X-NDVI-Cambio');
            const ndviInicio = responseDiff.headers.get('X-NDVI-Inicio');
            const ndviFin    = responseDiff.headers.get('X-NDVI-Fin');
            if (porcentaje !== null) setComparativaPorcentaje(parseFloat(porcentaje));
            if (ndviInicio !== null) setComparativaNdviInicio(parseFloat(ndviInicio));
            if (ndviFin    !== null) setComparativaNdviFin(parseFloat(ndviFin));

            setComparativaImageUrl(URL.createObjectURL(await responseDiff.blob()));
            setComparativaImgInicio(urlImgInicio);
            setComparativaImgFin(urlImgFin);
        } catch (e) {
            setErrorComparativa('Error de conexión al generar la comparativa');
        } finally {
            setLoadingComparativa(false);
        }
    };

    addLocale('es', {
        firstDayOfWeek: 1,
        dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
        dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
        dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
        monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
        monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
        today: 'Hoy',
        clear: 'Limpiar'
    });

    const isMobile = window.innerWidth < 700;

    // Mostrar estado de carga durante la validación
    if (isValidatingOwnership) {
        return (
            <PostLoginWrapper error="">
                <div className="flex flex-column justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                    <div className="loading"></div>
                    <p style={{ color: 'black', marginTop: '20px' }}>Validando acceso al campo...</p>
                </div>
            </PostLoginWrapper>
        );
    }

    // Mostrar error si no tiene acceso o no se encuentra el campo
    if (!field) {
        const errorTitle = error ? 'Acceso Denegado' : 'Campo no encontrado';
        const errorMessage = error || 'El campo solicitado no existe o no tienes acceso a él.';
        
        return (
            <PostLoginWrapper>
                <div className="flex flex-column justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <h2 style={{ color: '#dc3545', marginBottom: '15px' }}>{errorTitle}</h2>
                        <p style={{ color: 'black', fontSize: '16px', marginBottom: '20px' }}>
                            {errorMessage}
                        </p>
                    </div>
                </div>
            </PostLoginWrapper>
        );
    }

    const sinDatos = !field?.ndvi?.length && !field?.datos_clima?.historicos_clima?.length;

    return (
        <PostLoginWrapper error={error} isField={true} fieldId={fieldId} field={field}>
            {sinDatos ? (
                <div className='flex flex-column align-items-center justify-content-center' style={{ minHeight: '60vh', width: '100%' }}>
                    <div className='w-11 flex flex-column align-items-center gap-3 p-4' style={{ backgroundColor: '#fff8e1', borderRadius: '12px', border: '1px solid #ffe082', maxWidth: '480px', textAlign: 'center' }}>
                        <i className='pi pi-spin pi-spinner' style={{ color: '#f59e0b', fontSize: '32px' }} />
                        <span style={{ fontSize: '15px', color: '#92400e', lineHeight: '1.5' }}>
                            Los datos de este campo se están cargando en segundo plano.<br/>Vuelve en unos minutos y recarga la página.
                        </span>
                    </div>
                </div>
            ) : (
            <>
            {availableCampaignYears.length > 0 && (
                <div className="w-full flex justify-content-end align-items-center gap-2 mt-3 px-3">
                    <span style={{ color: '#374151', fontWeight: 500 }}>Campaña:</span>
                    <Dropdown
                        value={selectedCampaignYear}
                        options={availableCampaignYears.map(y => ({ label: `${y}`, value: y }))}
                        onChange={(e) => setSelectedCampaignYear(e.value)}
                        style={{ minWidth: '110px' }}
                        disabled={isLoading}
                    />
                </div>
            )}
            <Accordion multiple activeIndex={[0, 1]} className="w-full mt-5">
                <AccordionTab 
                    header={<span style={{ color: 'black', fontSize: '18px' }}>Índice de vegetación</span>}
                    pt={{
                        content: {
                            style: {
                                padding: 0
                            }
                        }
                    }}
                >
                    <div className="flex flex-column justify-content-center align-items-center">
                        {/* Pestañas: Imágenes / Comparativa */}
                        <div className="flex w-full" style={{ borderBottom: '2px solid #e5e7eb' }}>
                            <button
                                onClick={() => setShowComparativa(false)}
                                style={{
                                    flex: 1,
                                    padding: '10px 0',
                                    background: 'none',
                                    border: 'none',
                                    borderBottom: !showComparativa ? '3px solid #22c55e' : '3px solid transparent',
                                    color: !showComparativa ? '#16a34a' : '#6b7280',
                                    fontWeight: !showComparativa ? '700' : '400',
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    marginBottom: '-2px',
                                    transition: 'all 0.2s',
                                    textAlign: 'center'
                                }}
                            >
                                Imágenes
                            </button>
                            <button
                                onClick={() => setShowComparativa(true)}
                                style={{
                                    flex: 1,
                                    padding: '10px 0',
                                    background: 'none',
                                    border: 'none',
                                    borderBottom: showComparativa ? '3px solid #22c55e' : '3px solid transparent',
                                    color: showComparativa ? '#16a34a' : '#6b7280',
                                    fontWeight: showComparativa ? '700' : '400',
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    marginBottom: '-2px',
                                    transition: 'all 0.2s',
                                    textAlign: 'center'
                                }}
                            >
                                Hacer comparativa
                            </button>
                        </div>

                        {!showComparativa ? (
                            <>
                                {/* Leyenda NDVI */}
                                <div className='flex justify-content-evenly mt-4 w-full' style={{flexDirection: isMobile ? 'column' : 'row'}}>
                                    <div className='flex flex-column justify-content-center align-items-center'>
                                        <div className='flex justify-content-between w-full'>
                                            <span style={{ color: 'black', paddingLeft: '10px', fontSize: isMobile ? '14px' : '16px' }}>Bajo</span>
                                            <span style={{ color: 'black', fontSize: isMobile ? '14px' : '16px' }}>Medio</span>
                                            <span style={{ color: 'black', paddingRight: '10px', fontSize: isMobile ? '14px' : '16px' }}>Alto</span>
                                        </div>
                                        <img src={viñaImg} alt="Viña" width="95%" style={{ maxWidth: '500px' }} className='mt-2' />
                                    </div>
                                </div>
                                <div 
                                    className="images-container w-full" 
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {selectedImages.map((image, index) => (
                                        <div className="m-2" key={`${image.fecha}-${index}`}>
                                            <h3 style={{color: 'black', fontSize: '18px'}} className="text-center">{new Date(image.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}</h3>
                                            <img 
                                                src={image.url} 
                                                alt="Imagen de Sentinel" 
                                                style={{ 
                                                    display: 'block', 
                                                    margin: '0 auto', 
                                                    maxWidth: window.innerWidth < 425 ? '300px' : '400px'
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className={`flex justify-center ${currentNdviIndex >= sortedNdviNotInterpolated.length ? 'my-0' : 'my-4'}`}>
                                    {isLoading ? (
                                        <div className="loading" style={{marginTop: 80, marginBottom: 80}}></div>
                                    ) : (
                                        currentNdviIndex < sortedNdviNotInterpolated.length && (
                                            <Button onClick={handleNextNdvi} style={{ marginTop: '1rem' }}>
                                                Mostrar más imágenes
                                            </Button>
                                        )
                                    )}
                                </div>
                                {chartData && (
                                    <div className="flex flex-column justify-content-center mt-3 mb-5" style={{width: '95%'}}>
                                        <div>
                                            <Line style={{height: window.innerWidth < 500 ? '300px' : '400px'}} data={chartData} options={chartOptions} />
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="flex flex-column align-items-center w-full py-5 px-3" style={{ gap: '1.5rem' }}>
                                {/* Leyenda de colores de la comparativa */}
                                <div className="flex flex-column align-items-center w-full" style={{ maxWidth: '520px', gap: '6px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '95%' }}>
                                        <span style={{ color: 'black', fontSize: isMobile ? '13px' : '15px' }}>Decrecimiento</span>
                                        <span style={{ color: 'black', fontSize: isMobile ? '13px' : '15px' }}>Neutro</span>
                                        <span style={{ color: 'black', fontSize: isMobile ? '13px' : '15px' }}>Crecimiento</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', width: '95%' }}>
                                        {[
                                            '#660000', '#d90000', '#ff1a1a', '#ff8080',
                                            '#ffbf00', '#ffe600', '#ffff33',
                                            '#66ff66', '#00cc00', '#009900', '#006600'
                                        ].map((color, i) => (
                                            <div key={i} style={{
                                                flex: 1,
                                                aspectRatio: '1',
                                                backgroundColor: color,
                                                borderRadius: '6px',
                                                border: '1px solid rgba(0,0,0,0.1)'
                                            }} />
                                        ))}
                                    </div>
                                </div>

                                <h3 style={{ color: 'black', fontSize: '18px', fontWeight: '600', margin: 0 }}>
                                    Comparativa de imágenes
                                </h3>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        width: '100%',
                                        maxWidth: '520px'
                                    }}
                                >
                                    {/* Fila fechas: siempre dos columnas lado a lado */}
                                    <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
                                        <div className="flex flex-column" style={{ gap: '4px', flex: 1, minWidth: 0 }}>
                                            <label style={{ color: '#374151', fontSize: '13px', fontWeight: '500' }}>
                                                Fecha inicio
                                            </label>
                                            <Dropdown
                                                value={comparativaFechaInicio}
                                                options={availableNdviDateOptionsAsc}
                                                onChange={(e) => setComparativaFechaInicio(e.value)}
                                                placeholder="Selecciona"
                                                style={{ width: '100%' }}
                                                panelStyle={{ maxHeight: '250px' }}
                                            />
                                        </div>
                                        <div className="flex flex-column" style={{ gap: '4px', flex: 1, minWidth: 0 }}>
                                            <label style={{ color: '#374151', fontSize: '13px', fontWeight: '500' }}>
                                                Fecha fin
                                            </label>
                                            <Dropdown
                                                value={comparativaFechaFin}
                                                options={availableNdviDateOptionsDesc}
                                                onChange={(e) => setComparativaFechaFin(e.value)}
                                                placeholder="Selecciona"
                                                style={{ width: '100%' }}
                                                panelStyle={{ maxHeight: '250px' }}
                                            />
                                        </div>
                                    </div>
                                    {/* Botón centrado debajo */}
                                    <Button
                                        label="Calcular"
                                        icon="pi pi-calculator"
                                        style={{ whiteSpace: 'nowrap', alignSelf: 'center' }}
                                        disabled={!comparativaFechaInicio || !comparativaFechaFin || loadingComparativa}
                                        loading={loadingComparativa}
                                        onClick={handleCalcularComparativa}
                                    />
                                </div>

                                {errorComparativa && (
                                    <div style={{ color: '#dc2626', fontSize: '14px', marginTop: '0.5rem' }}>
                                        {errorComparativa}
                                    </div>
                                )}

                                {loadingComparativa && (
                                    <div className="loading" style={{ marginTop: 40, marginBottom: 40 }}></div>
                                )}

                                {comparativaImageUrl && !loadingComparativa && (
                                    <div className="flex flex-column align-items-center w-full mt-3" style={{ gap: '16px' }}>

                                        {/* Fila superior: % + imagen diferencia */}
                                        <div className="flex flex-column align-items-center" style={{ gap: '10px' }}>
                                            {comparativaPorcentaje !== null && (
                                                <div style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    padding: '10px 20px',
                                                    borderRadius: '10px',
                                                    backgroundColor: comparativaPorcentaje >= 0 ? '#f0fdf4' : '#fef2f2',
                                                    border: `1px solid ${comparativaPorcentaje >= 0 ? '#86efac' : '#fca5a5'}`
                                                }}>
                                                    <span style={{ fontSize: '26px', fontWeight: '700', color: comparativaPorcentaje >= 0 ? '#16a34a' : '#dc2626' }}>
                                                        {comparativaPorcentaje >= 0 ? '▲' : '▼'} {Math.abs(comparativaPorcentaje)}%
                                                    </span>
                                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                                        {comparativaPorcentaje >= 0 ? 'Crecimiento' : 'Decrecimiento'} del índice de vegetación
                                                    </span>
                                                </div>
                                            )}
                                            <h4 style={{ color: 'black', fontSize: '14px', fontWeight: '600', margin: 0 }}>Diferencia</h4>
                                            <img
                                                src={comparativaImageUrl}
                                                alt="Diferencia de índice de vegetación"
                                                style={{ display: 'block', margin: '0 auto', maxWidth: window.innerWidth < 425 ? '300px' : '400px' }}
                                            />
                                        </div>

                                        {/* Fila inferior: imagen fecha fin (izq) + imagen fecha inicio (der) */}
                                        <div className="flex justify-content-center" style={{ gap: '16px', flexWrap: 'wrap' }}>
                                            {comparativaImgFin && (
                                                <div className="flex flex-column align-items-center" style={{ gap: '6px' }}>
                                                    <h4 style={{ color: 'black', fontSize: '14px', fontWeight: '600', margin: 0 }}>
                                                        {new Date(comparativaFechaFin).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                    </h4>
                                                    {comparativaNdviFin !== null && (
                                                        <span style={{ fontSize: '12px', color: '#6b7280' }}>NDVI: {comparativaNdviFin}</span>
                                                    )}
                                                    <img
                                                        src={comparativaImgFin}
                                                        alt={`Imagen ${comparativaFechaFin}`}
                                                        style={{ display: 'block', maxWidth: window.innerWidth < 425 ? '140px' : '190px' }}
                                                    />
                                                </div>
                                            )}
                                            {comparativaImgInicio && (
                                                <div className="flex flex-column align-items-center" style={{ gap: '6px' }}>
                                                    <h4 style={{ color: 'black', fontSize: '14px', fontWeight: '600', margin: 0 }}>
                                                        {new Date(comparativaFechaInicio).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                    </h4>
                                                    {comparativaNdviInicio !== null && (
                                                        <span style={{ fontSize: '12px', color: '#6b7280' }}>NDVI: {comparativaNdviInicio}</span>
                                                    )}
                                                    <img
                                                        src={comparativaImgInicio}
                                                        alt={`Imagen ${comparativaFechaInicio}`}
                                                        style={{ display: 'block', maxWidth: window.innerWidth < 425 ? '140px' : '190px' }}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                )}

                                {chartData && (
                                    <div className="flex flex-column justify-content-center mt-3 mb-5" style={{ width: '95%' }}>
                                        <div>
                                            <Line style={{ height: window.innerWidth < 500 ? '300px' : '400px' }} data={chartData} options={chartOptions} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </AccordionTab>
                <AccordionTab 
                    header={<span style={{ color: 'black', fontSize: '18px' }}>Riego</span>}
                    pt={{
                        content: {
                            style: {
                                padding: 0
                            }
                        }
                    }}
                >
                    <div className="flex flex-column justify-content-center align-items-center">
                        <h2 style={{color: 'black', fontSize: '18px'}}>Datos históricos</h2>
                        {field && field.datos_clima?.historicos_clima && (
                            <div className="flex justify-content-center">
                                <Calendar 
                                    inputStyle={{color: 'black'}}
                                    value={selectedDateRange} 
                                    onChange={(e) => {
                                        const { minDate, maxDate } = campaignCalendarBounds;
                                        const clamp = (d) => {
                                            if (!d) return d;
                                            const t = d.getTime();
                                            if (t < minDate.getTime()) return new Date(minDate);
                                            if (t > maxDate.getTime()) return new Date(maxDate);
                                            return d;
                                        };
                                        const v = e.value;
                                        if (!v) {
                                            setSelectedDateRange(v);
                                            return;
                                        }
                                        if (Array.isArray(v)) {
                                            setSelectedDateRange(v.map((d) => (d ? clamp(d) : d)));
                                        } else {
                                            setSelectedDateRange(clamp(v));
                                        }
                                    }}
                                    minDate={campaignCalendarBounds.minDate}
                                    maxDate={campaignCalendarBounds.maxDate}
                                    selectionMode="range" 
                                    readOnlyInput
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    placeholder="Selecciona fecha"
                                    locale="es"
                                />
                            </div>
                        )}
                        {chartDataClima && (
                            <div className="flex justify-content-center mt-2" style={{width: '95%'}}>
                                <Bar style={{height: window.innerWidth < 500 ? '300px' : '400px'}}  data={chartDataClima} options={chartOptionsClima} />
                            </div>
                        )}
                        
                        {/* Gráfica de Balance Hídrico */}
                        <h2 style={{color: 'black', fontSize: '18px', marginTop: '30px'}}>Balance hídrico del suelo y del cultivo</h2>
                        {chartDataBalance && (
                            <div className="flex justify-content-center mt-2" style={{width: '95%'}}>
                                <Line style={{height: window.innerWidth < 500 ? '350px' : '450px'}} data={chartDataBalance} options={chartOptionsBalance} />
                            </div>
                        )}
                        {/* Gráfica de Balance Hídrico V2*/}
                        <h2 style={{color: 'black', fontSize: '18px', marginTop: '30px', display: localStorage.getItem('role') === '1' ? 'block' : 'none'}} >Balance hídrico del suelo y del cultivo V2</h2>
                        {chartDataBalanceV2 && localStorage.getItem('role') === '1' && (
                            <div className="flex justify-content-center mt-2" style={{width: '95%'}}>
                                <Line style={{height: window.innerWidth < 500 ? '350px' : '450px'}} data={chartDataBalanceV2} options={chartOptionsBalanceV2} />
                            </div>
                        )}
                        {field?.datos_clima?.predicciones?.length > 0 && (
                            <>
                            <h2 style={{color: 'black', fontSize: '18px'}}>Predicciones a 15 días</h2>
                            <div style={{width: '95%'}}>
                                <div className="flex justify-content-center">
                                    <DataTable className='w-full custom-datatable' value={datosClimaConDeficit} responsiveLayout="scroll">
                                        <Column
                                            field="fecha"
                                            header={<div style={{ textAlign: 'center', width: '100%', color: 'black' }}>Fecha</div>}
                                            body={fechaClimaBodyTemplate}
                                            style={{ width: '20%' }}
                                            headerStyle={{ textAlign: 'center', color: 'black' }}
                                            bodyStyle={{ textAlign: 'center', color: 'black' }}
                                        />
                                        <Column
                                            field="et0"
                                            header={
                                                <div style={{ textAlign: 'center', width: '100%', color: 'black' }}>
                                                    <img src={et0Img} alt="ET0" width="30" height="30" />
                                                </div>
                                            }
                                            body={et0BodyTemplate}
                                            style={{ width: '20%' }}
                                            headerStyle={{ textAlign: 'center', color: 'black' }}
                                            bodyStyle={{ textAlign: 'center', color: 'black' }}
                                        />
                                        <Column
                                            field="precipitacion"
                                            header={
                                                <div style={{ textAlign: 'center', width: '100%', color: 'black' }}>
                                                    <img src={lluviaImg} alt="Lluvia" width="30" height="30" />
                                                </div>
                                            }
                                            body={precipitacionBodyTemplate}
                                            style={{ width: '20%' }}
                                            headerStyle={{ textAlign: 'center', color: 'black' }}
                                            bodyStyle={{ textAlign: 'center', color: 'black' }}
                                        />
                                        <Column
                                            field="resultado"
                                            header={
                                                <div style={{ textAlign: 'center', width: '100%', color: 'black' }}>
                                                    <img src={riegoImg} alt="Necesidad de Riego" width="30" height="30" />
                                                </div>
                                            }
                                            body={et0LessPrecipitacionBodyTemplate}
                                            style={{ width: '20%' }}
                                            headerStyle={{ textAlign: 'center', color: 'black' }}
                                            bodyStyle={{ textAlign: 'center', color: 'black' }}
                                        />
                                    </DataTable>
                                </div>
                                <div className="flex justify-content-center text-center">
                                    <h2 style={{color: 'black', fontSize: '18px'}}>La evapotranspiración de los proximos 7 días se estima en {totalEt0} mm.</h2>
                                </div>
                            </div>
                            </>
                        )}
                        
                        {/* Botones para añadir riego y ver histórico */}
                        <div className="flex justify-content-center gap-3 mt-4 mb-4">
                            <Button 
                                icon="pi pi-plus" 
                                label="Añadir Riego" 
                                onClick={() => setShowRiegoModal(true)}
                                className="p-button-success"
                            />
                            <Button 
                                icon="pi pi-list" 
                                label="Ver Histórico" 
                                onClick={handleShowHistorico}
                                className="p-button-info"
                            />
                        </div>
                    </div>
                </AccordionTab>
            </Accordion>

            {/* Modal para añadir/editar riego */}
            <Dialog 
                header={isEditMode ? "Editar Riego" : "Añadir Riego"} 
                visible={showRiegoModal} 
                style={{ width: '400px' }} 
                onHide={() => {
                    handleCloseRiegoModal();
                    // Reactivar scroll del body
                    document.body.style.overflow = 'auto';
                }}
                onShow={() => {
                    // Desactivar scroll del body al mostrar modal
                    document.body.style.overflow = 'hidden';
                }}
                footer={
                    <div>
                        <Button 
                            label="Cancelar" 
                            icon="pi pi-times" 
                            onClick={handleCloseRiegoModal} 
                            className="p-button-text" 
                        />
                        <Button 
                            label={isEditMode ? "Actualizar" : "Guardar"} 
                            icon="pi pi-check" 
                            onClick={isEditMode ? handleUpdateRiego : handleAddRiego} 
                            disabled={loadingRiego}
                            loading={loadingRiego}
                        />
                    </div>
                }
            >
                <div className="flex flex-column gap-3">
                    <div>
                        <label htmlFor="fechaRiego" style={{ color: 'black', display: 'block', marginBottom: '0.5rem' }}>
                            Fecha del riego:
                        </label>
                        <Calendar 
                            id="fechaRiego"
                            value={fechaRiego} 
                            onChange={(e) => setFechaRiego(e.value)} 
                            dateFormat="dd/mm/yy"
                            showIcon
                            locale="es"
                            style={{ width: '100%' }}
                            inputStyle={{ color: 'black' }}
                        />
                    </div>
                    <div>
                        <label htmlFor="cantidadRiego" style={{ color: 'black', display: 'block', marginBottom: '0.5rem' }}>
                            Cantidad (mm):
                        </label>
                        <InputNumber 
                            id="cantidadRiego"
                            value={cantidadRiego} 
                            onValueChange={(e) => setCantidadRiego(e.value)} 
                            min={0}
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            suffix=" mm"
                            placeholder="Ingrese la cantidad de riego"
                            style={{ width: '100%' }}
                            inputStyle={{ color: 'black' }}
                        />
                    </div>
                </div>
            </Dialog>

            {/* Modal para mostrar histórico de riego */}
            <Dialog 
                header="Histórico de Riegos" 
                visible={showHistoricoRiego} 
                style={{ width: '100%', height: '100%', maxHeight: '100%', margin: '0' }} 
                onHide={() => {
                    setShowHistoricoRiego(false);
                    // Reactivar scroll del body
                    document.body.style.overflow = 'auto';
                }}
                pt={{
                    content: {
                        style: {
                            padding: 0
                        }
                    }
                }}
            >
                {loadingRiego ? (
                    <div className="flex justify-content-center">
                        <div className="loading" style={{marginTop: 50, marginBottom: 50}}></div>
                    </div>
                ) : (
                    <div style={{ width: '100%' }}>
                        {historicoRiegoCampania.length > 0 ? (
                            <DataTable
                                value={historicoRiegoCampania}
                                responsiveLayout="scroll"
                                className="custom-datatable"
                                sortField="fecha"
                                sortOrder={-1}
                            >
                                <Column
                                    field="fecha"
                                    header={<div style={{ textAlign: 'center', width: '100%', color: 'black' }}>Fecha</div>}
                                    body={fechaRiegoBodyTemplate}
                                    style={{ width: '35%' }}
                                    headerStyle={{ textAlign: 'center', color: 'black' }}
                                    bodyStyle={{ textAlign: 'center', color: 'black' }}
                                    sortable
                                />
                                <Column
                                    field="cantidad"
                                    header={<div style={{ textAlign: 'center', width: '100%', color: 'black' }}>Cantidad</div>}
                                    body={cantidadRiegoBodyTemplate}
                                    style={{ width: '35%' }}
                                    headerStyle={{ textAlign: 'center', color: 'black' }}
                                    bodyStyle={{ textAlign: 'center', color: 'black' }}
                                    sortable
                                />
                                <Column
                                    header={<div style={{ textAlign: 'center', width: '100%', color: 'black' }}>Acciones</div>}
                                    body={accionesRiegoBodyTemplate}
                                    style={{ width: '30%' }}
                                    headerStyle={{ textAlign: 'center', color: 'black' }}
                                    bodyStyle={{ textAlign: 'center', color: 'black' }}
                                />
                            </DataTable>
                        ) : (
                            <div className="text-center">
                                <p style={{ color: 'black', fontSize: '16px' }}>No hay riegos registrados para este campo.</p>
                            </div>
                        )}
                    </div>
                )}
            </Dialog>

            {/* Modal de confirmación para borrar riego */}
            <Dialog 
                header="Confirmar eliminación" 
                visible={showDeleteConfirm} 
                style={{ width: '90%', maxWidth: '450px' }} 
                onHide={() => {
                    handleCancelDelete();
                    // Reactivar scroll del body
                    document.body.style.overflow = 'auto';
                }}
                onShow={() => {
                    // Desactivar scroll del body al mostrar modal
                    document.body.style.overflow = 'hidden';
                }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button 
                            label="Cancelar" 
                            icon="pi pi-times" 
                            onClick={handleCancelDelete} 
                            className="p-button-text p-button-secondary" 
                        />
                        <Button 
                            label="Eliminar" 
                            icon="pi pi-trash" 
                            onClick={handleConfirmDelete} 
                            className="p-button-danger"
                            disabled={loadingRiego}
                            loading={loadingRiego}
                        />
                    </div>
                }
                className="confirm-dialog"
            >
                <div className="flex align-items-center">
                    <div>
                        <p style={{ color: 'black', fontSize: '16px', margin: '0 0 10px 0' }}>
                            ¿Estás seguro de que quieres eliminar este riego?
                        </p>
                        {riegoToDelete && (
                            <div style={{ 
                                backgroundColor: '#f8f9fa', 
                                padding: '10px', 
                                borderRadius: '6px',
                                border: '1px solid #e9ecef'
                            }}>
                                <p style={{ color: '#6c757d', fontSize: '14px', margin: '0' }}>
                                    <strong>Fecha:</strong> {new Date(riegoToDelete.fecha).toLocaleDateString('es-ES', { 
                                        day: '2-digit', 
                                        month: '2-digit', 
                                        year: 'numeric' 
                                    })}
                                </p>
                                <p style={{ color: '#6c757d', fontSize: '14px', margin: '5px 0 0 0' }}>
                                    <strong>Cantidad:</strong> {riegoToDelete.cantidad} mm
                                </p>
                            </div>
                        )}
                        <p style={{ color: '#dc3545', fontSize: '14px', margin: '10px 0 0 0', fontWeight: '500' }}>
                            Esta acción no se puede deshacer.
                        </p>
                    </div>
                </div>
            </Dialog>
            </>
            )}
        </PostLoginWrapper>
    );
};
